import { evaluateExpression } from '@ayumia/core/expressions';
import { toServiceError } from './instance.js';
import { isViewBodyV2, migrateViewBody } from './migrate-view-body.js';
import { parseService } from './services.js';
const CORE_VIEW_COMPONENTS = new Set(['Table', 'CardList', 'Kanban', 'Agenda', 'WeekGrid', 'Detail', 'Form', 'Dashboard']);
/** Corps lu en base : une vue 2.x encore active est convertie à la lecture (ADR-099), jamais réécrite. */
export function normalizeViewBody(stored) {
    return isViewBodyV2(stored) ? migrateViewBody(stored) : stored;
}
/** Garde de type : une représentation du composant Core `component` (un composite publié n'en est jamais une). */
export function isCoreRepresentation(representation, component) {
    return representation.component === component && CORE_VIEW_COMPONENTS.has(component);
}
/** Représentations d'un composant Core, dans l'ordre de la vue. */
export function representationsOf(body, component) {
    return body.representations.filter((representation) => isCoreRepresentation(representation, component));
}
/** Représentation d'entrée (`entry`). */
export function entryRepresentation(body) {
    return body.representations.find((representation) => representation.id === body.entry);
}
/** Entrées du conteneur `Collection` ; vides pour une vue sans représentation de collection. */
export function collectionInputs(body) {
    return body.collection?.inputs ?? {};
}
/** Nom de la commande d'une entrée de `bulk_commands`, quelle que soit sa forme. */
export function bulkCommandName(entry) {
    return typeof entry === 'string' ? entry : entry.command_name;
}
/** Toutes les liaisons d'événements d'une vue : conteneur, représentations, colonnes du Kanban. */
export function viewBindings(body) {
    const out = [];
    const collect = (bindings, path) => {
        for (const [event, binding] of Object.entries(bindings ?? {}))
            out.push({ path: `${path}/${event}`, event, actions: binding.actions });
    };
    collect(body.collection?.events, '/collection/events');
    body.representations.forEach((representation, index) => {
        collect(representation.events, `/representations/${index}/events`);
        if (isCoreRepresentation(representation, 'Kanban')) {
            representation.inputs.columns.forEach((column, c) => collect(column.events, `/representations/${index}/inputs/columns/${c}/events`));
        }
    });
    return out;
}
/** Actions d'une liaison, suites comprises (`on_success`, `on_error`), avec leur pointeur. */
export function flattenActions(actions, path) {
    const out = [];
    actions.forEach((action, index) => {
        const at = `${path}/${index}`;
        out.push({ path: at, action });
        if (action.action === 'api') {
            if (action.on_success !== undefined)
                out.push(...flattenActions(action.on_success, `${at}/on_success`));
            if (action.on_error !== undefined)
                out.push(...flattenActions(action.on_error, `${at}/on_error`));
        }
    });
    return out;
}
/** Commandes citées par les liaisons d'une vue (`api` vers `command:<Nom>`), avec leur pointeur. */
export function bindingCommands(body) {
    const out = [];
    for (const binding of viewBindings(body)) {
        for (const { path, action } of flattenActions(binding.actions, `${binding.path}/actions`)) {
            if (action.action !== 'api')
                continue;
            const service = parseService(action.service);
            if (service?.kind === 'command')
                out.push({ path: `${path}/service`, command: service.name });
        }
    }
    return out;
}
/**
 * Commandes que la vue présente hors de `actions` : création, masse, liaisons (dépôt du Kanban…).
 * Leur charge utile déclarée est servie par `GetView` ; chacune se réautorise à l'exécution.
 */
export function viewCommandNames(body) {
    const inputs = collectionInputs(body);
    const names = [
        ...(inputs.create_action?.command_name === undefined ? [] : [inputs.create_action.command_name]),
        ...(inputs.bulk_commands ?? []).map(bulkCommandName),
        ...bindingCommands(body).map((entry) => entry.command),
    ];
    return [...new Set(names)];
}
/** Paramètres d'une action `api` de vue, évalués sur `$event`, `$record` et `$context` (pré-remplissage d'un formulaire de commande). */
export function bindingParams(action, event, options = {}) {
    const scope = { event, record: options.record ?? null, context: options.context ?? {} };
    const params = {};
    for (const [name, expression] of Object.entries(action.params ?? {}))
        params[name] = evaluateExpression(expression, scope, options.functions === undefined ? {} : { functions: options.functions });
    return params;
}
/**
 * Exécute une liaison d'événement de vue (ADR-099) : une vue n'a pas d'état local, une liaison y
 * est une suite d'actions `api` vers une commande, une requête ou un service de coquille, avec
 * leurs suites `on_success` et `on_error`. La commande part par la passerelle de l'hôte et se
 * réautorise au serveur : la liaison ne décide d'aucun accès.
 */
export async function runViewBinding(actions, event, options = {}) {
    for (const action of actions) {
        if (action.action !== 'api')
            return { status: 'failed', error: { category: 'CONFIGURATION_ERROR', code: 'VIEW_BINDING_ACTION_UNSUPPORTED', message: `Une liaison de vue exécute une action api, pas « ${action.action} ».` } };
        const service = parseService(action.service);
        if (service === null || service.kind === 'emit')
            return { status: 'failed', error: { category: 'CONFIGURATION_ERROR', code: 'VIEW_BINDING_SERVICE_UNKNOWN', message: `« ${action.service} » n'est pas un service de vue.` } };
        let params;
        try {
            params = bindingParams(action, event, options);
        }
        catch (caught) {
            return { status: 'failed', error: toServiceError(caught) };
        }
        const unavailable = (code, message) => ({ ok: false, error: { category: 'CONFIGURATION_ERROR', code, message } });
        const outcome = service.kind === 'shell'
            ? options.shell === undefined
                ? unavailable('SHELL_UNAVAILABLE', 'Aucune coquille pour cette vue.')
                : await options.shell.call(service.name, params)
            : options.gateway === undefined
                ? unavailable('GATEWAY_UNAVAILABLE', 'Aucune passerelle de services.')
                : await options.gateway.call(service.kind, service.name, params);
        if (!outcome.ok) {
            await runViewBinding(action.on_error ?? [], { error: outcome.error }, options);
            return { status: 'failed', error: outcome.error };
        }
        const next = await runViewBinding(action.on_success ?? [], { value: outcome.value }, options);
        if (next.status === 'failed')
            return next;
    }
    return { status: 'completed' };
}
//# sourceMappingURL=view-assembly.js.map